import { useState, useEffect, useRef } from 'react';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../../database/config';
import { useHomeData } from '../../../database/HomeDataContext';
import { Body7Skeleton } from '../../Skeletons/Skeletons';
import { FaStar, FaStarHalfAlt, FaRegStar } from 'react-icons/fa';

const Body7 = ({ preloadedData }) => {
    const { data: homeData, isOutsideProvider } = useHomeData();
    const [localData, setLocalData] = useState(null);

    // --- Scroll & Drag State ---
    const scrollContainerRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isPaused, setIsPaused] = useState(false);

    // Performance Refs (Prevents re-renders and layout thrashing during drag)
    const dragStartX = useRef(0);
    const dragScrollLeft = useRef(0);
    const setWidthRef = useRef(0); // Stores the exact width of one set of cards
    const animationRef = useRef(null);

    // Fallback fetch for when used outside HomeDataProvider (e.g. About page)
    useEffect(() => {
        if (!isOutsideProvider) return;
        const fetchBody7Data = async () => {
            try {
                const [contentSnap, testimonialsSnap] = await Promise.all([
                    getDoc(doc(db, 'home', 'customerReviewsSection')),
                    getDocs(collection(db, 'home', 'customerReviewsSection', 'cards')),
                ]);
                const testimonials = [];
                testimonialsSnap.forEach((d) => {
                    const data = d.data();
                    testimonials.push({
                        id: d.id,
                        name: data.name || '',
                        role: data.location || '',
                        rating: parseInt(data.starRating) || 5,
                        quote: data.review || '',
                        image: data.image || '',
                    });
                });
                testimonials.sort((a, b) => a.id.localeCompare(b.id));

                let heading = '', subHeading = '', footerText = '';
                if (contentSnap.exists()) {
                    const data = contentSnap.data();
                    heading = data.heading || '';
                    subHeading = data.subHeading || '';
                    footerText = data.belowLine || '';
                }
                setLocalData({ heading, subHeading, footerText, testimonials });
            } catch (error) {
                console.error('Error fetching Body7 data:', error);
            }
        };
        fetchBody7Data();
    }, [isOutsideProvider]);

    const body7Data = preloadedData || (isOutsideProvider ? localData : homeData?.body7);
    const displayTestimonials = body7Data?.testimonials || [];

    const hasMarquee = displayTestimonials.length >= 3;
    
    // Quintuple the array (5 sets) to create a massive, unbreakable buffer zone
    const marqueeItems = hasMarquee
        ? [
            ...displayTestimonials, 
            ...displayTestimonials, 
            ...displayTestimonials, 
            ...displayTestimonials, 
            ...displayTestimonials
          ]
        : displayTestimonials;

    // --- Width Calculation ---
    useEffect(() => {
        if (!hasMarquee || !scrollContainerRef.current) return;
        
        const updateWidth = () => {
            const container = scrollContainerRef.current;
            // Total width divided by 5 sets gives us the exact pixel width of one set
            setWidthRef.current = container.scrollWidth / 5;
            
            // On initial load, jump the user silently to the exact middle (Set 3)
            if (container.scrollLeft === 0 && setWidthRef.current > 0) {
                container.scrollLeft = setWidthRef.current * 2;
            }
        };

        // Run on mount, wait a tick for layout, then calculate
        setTimeout(updateWidth, 50);
        window.addEventListener('resize', updateWidth);
        
        return () => window.removeEventListener('resize', updateWidth);
    }, [hasMarquee, marqueeItems.length]);

    // --- Animation Loop ---
    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (!hasMarquee || !scrollContainer || displayTestimonials.length === 0) return;

        const animate = () => {
            if (!isDragging && !isPaused && setWidthRef.current > 0) {
                // Speed: 0.8
                scrollContainer.scrollLeft += 0.8;

                // If auto-scroll reaches Set 4, seamlessly jump back to Set 3
                if (scrollContainer.scrollLeft >= setWidthRef.current * 3) {
                    scrollContainer.scrollLeft -= setWidthRef.current;
                }
            }
            animationRef.current = requestAnimationFrame(animate);
        };

        animationRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
        };
    }, [isDragging, isPaused, hasMarquee, displayTestimonials.length]);

    // --- Optimized Event Handlers ---
    const handleDragStart = (clientX) => {
        if (!scrollContainerRef.current) return;
        setIsDragging(true);
        setIsPaused(true);
        dragStartX.current = clientX - scrollContainerRef.current.offsetLeft;
        dragScrollLeft.current = scrollContainerRef.current.scrollLeft;
    };

    const handleDragMove = (clientX) => {
        if (!isDragging || !scrollContainerRef.current || setWidthRef.current === 0) return;
        
        const container = scrollContainerRef.current;
        const x = clientX - container.offsetLeft;
        const walk = x - dragStartX.current; // 1:1 speed ratio

        let newScrollLeft = dragScrollLeft.current - walk;
        const setWidth = setWidthRef.current;

        // BOUNDARY LOGIC: Keep the user perpetually in the middle zones
        // If they drag right (scroll left) into Set 2:
        if (newScrollLeft <= setWidth * 1) {
            newScrollLeft += setWidth;
            dragStartX.current = x; 
            dragScrollLeft.current = newScrollLeft;
        } 
        // If they drag left (scroll right) into Set 4:
        else if (newScrollLeft >= setWidth * 3) {
            newScrollLeft -= setWidth;
            dragStartX.current = x; 
            dragScrollLeft.current = newScrollLeft;
        }

        container.scrollLeft = newScrollLeft;
    };

    const handleDragEnd = () => {
        setIsDragging(false);
        setTimeout(() => setIsPaused(false), 1500);
    };

    // Mouse & Touch bindings
    const handleMouseDown = (e) => { e.preventDefault(); handleDragStart(e.pageX); };
    const handleMouseMove = (e) => { if (isDragging) { e.preventDefault(); handleDragMove(e.pageX); }};
    const handleTouchStart = (e) => handleDragStart(e.touches[0].pageX);
    const handleTouchMove = (e) => handleDragMove(e.touches[0].pageX);

    // --- Helper Components ---
    const renderStars = (rating) => {
        const stars = [];
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 !== 0;
        for (let i = 0; i < fullStars; i++) stars.push(<FaStar key={`full-${i}`} className="text-orange-400 text-xs sm:text-base" />);
        if (hasHalfStar) stars.push(<FaStarHalfAlt key="half" className="text-orange-400 text-xs sm:text-base" />);
        const emptyStars = 5 - Math.ceil(rating);
        for (let i = 0; i < emptyStars; i++) stars.push(<FaRegStar key={`empty-${i}`} className="text-orange-300/50 text-xs sm:text-base" />);
        return stars;
    };

    const TestimonialCard = ({ testimonial, isMarquee }) => (
        <div
            className={`bg-white rounded-3xl p-5 sm:p-8 border border-orange-100/50 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-300
            ${isMarquee
                    ? 'w-[280px] sm:w-[380px] md:w-[420px] flex-shrink-0'
                    : 'h-full'
                }`}
        >
            <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-5">
                <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full overflow-hidden bg-orange-50 flex-shrink-0 ring-2 sm:ring-4 ring-orange-50">
                    {testimonial.image ? (
                        <img
                            src={testimonial.image}
                            alt={testimonial.name}
                            className="w-full h-full object-cover select-none pointer-events-none"
                            draggable="false"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-orange-400 font-bold text-lg sm:text-xl select-none">
                            {testimonial.name.charAt(0)}
                        </div>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-gray-900 text-base sm:text-lg leading-tight truncate pointer-events-none">
                        {testimonial.name}
                    </h4>
                    <p className="text-gray-400 text-xs sm:text-sm mt-0.5 truncate pointer-events-none">
                        {testimonial.role}
                    </p>
                </div>
            </div>
            <div className="flex gap-1 mb-3 sm:mb-4 select-none">
                {renderStars(testimonial.rating)}
            </div>
            <p className="text-gray-600/90 text-sm sm:text-[16px] leading-relaxed italic select-none pointer-events-none">
                "{testimonial.quote}"
            </p>
        </div>
    );

    if (!body7Data || displayTestimonials.length === 0) {
        return <Body7Skeleton />;
    }

    return (
        <section className="py-16 sm:py-24 bg-[#FFFCF8] overflow-hidden select-none">
            {/* Header */}
            <div className="max-w-4xl mx-auto text-center mb-10 sm:mb-16 px-6">
                <p className="text-orange-500 font-bold tracking-[0.2em] text-xs sm:text-sm mb-3 sm:mb-4 uppercase">
                    HAPPY SUBSCRIBERS
                </p>
                <h2 className="text-3xl sm:text-5xl md:text-6xl font-black text-gray-900 mb-4 sm:mb-6 tracking-tight">
                    {body7Data.heading}
                </h2>
                <p className="text-gray-500 text-base sm:text-xl max-w-2xl mx-auto leading-relaxed font-medium">
                    {body7Data.subHeading}
                </p>
            </div>

            {/* Testimonials */}
            {hasMarquee ? (
                <div className="relative w-full">
                    <div
                        ref={scrollContainerRef}
                        // Conditionally apply horizontal padding to fix infinite scroll math
                        className={`flex gap-4 sm:gap-6 pb-10 overflow-x-auto scrollbar-hide ${hasMarquee ? '' : 'px-4'} ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
                        style={{
                            scrollBehavior: 'auto',
                        }}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleDragEnd}
                        onMouseLeave={handleDragEnd}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleDragEnd}
                    >
                        {marqueeItems.map((testimonial, index) => (
                            <TestimonialCard
                                key={`${testimonial.id}-${index}`}
                                testimonial={testimonial}
                                isMarquee={true}
                            />
                        ))}
                    </div>

                    {/* Gradients */}
                    <div className="absolute left-0 top-0 bottom-10 w-16 sm:w-32 bg-gradient-to-r from-[#FFFCF8] to-transparent pointer-events-none z-10" />
                    <div className="absolute right-0 top-0 bottom-10 w-16 sm:w-32 bg-gradient-to-l from-[#FFFCF8] to-transparent pointer-events-none z-10" />
                </div>
            ) : (
                <div className="max-w-7xl mx-auto px-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {displayTestimonials.map((testimonial) => (
                            <TestimonialCard
                                key={testimonial.id}
                                testimonial={testimonial}
                                isMarquee={false}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Footer */}
            <div className="text-center mt-2 sm:mt-4 px-6">
                <p className="text-gray-500 font-medium text-sm sm:text-base">
                    {body7Data.footerText}
                </p>
            </div>

            <style jsx>{`
                .scrollbar-hide {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
            `}</style>
        </section>
    );
};

export default Body7;